import type { QueryClient } from "@tanstack/react-query";

import { GuiMessageBroker } from "./GuiMessageBroker";
import { NavigationManager } from "./internal/NavigationManager";
import { WorkbenchSessionManager } from "./internal/WorkbenchSession/WorkbenchSessionManager";

/**
 * Main workbench coordinator.
 *
 * Delegates responsibilities:
 * - Session management -> WorkbenchSessionManager
 * - Navigation -> NavigationManager
 * - Persistence -> PersistenceOrchestrator (via SessionManager)
 * - GUI state -> GuiMessageBroker
 */
export class Workbench {
    private readonly _guiMessageBroker: GuiMessageBroker;
    private readonly _queryClient: QueryClient;
    private readonly _sessionManager: WorkbenchSessionManager;
    private readonly _navigationManager: NavigationManager;
    private _isInitialized: boolean = false;

    constructor(queryClient: QueryClient) {
        this._queryClient = queryClient;
        this._guiMessageBroker = new GuiMessageBroker();
        this._sessionManager = new WorkbenchSessionManager(this, queryClient, this._guiMessageBroker);

        // Create NavigationManager instance and register callbacks
        this._navigationManager = new NavigationManager();
        this._navigationManager.setOnBeforeUnload(() => this.handleBeforeUnload());
        this._navigationManager.setOnNavigate(async () => await this.handleNavigation());
    }

    getQueryClient(): QueryClient {
        return this._queryClient;
    }

    getSessionManager(): WorkbenchSessionManager {
        return this._sessionManager;
    }

    getGuiMessageBroker(): GuiMessageBroker {
        return this._guiMessageBroker;
    }

    getNavigationManager(): NavigationManager {
        return this._navigationManager;
    }

    /**
     * Handle beforeunload event - check if there are unsaved changes.
     * Returns true if navigation should be blocked (show warning dialog).
     */
    private handleBeforeUnload(): boolean {
        return this._sessionManager.hasDirtyChanges();
    }

    /**
     * Handle browser navigation (back/forward buttons).
     * Delegates to SessionManager for actual handling.
     * Returns true if navigation should proceed, false to cancel.
     */
    private async handleNavigation(): Promise<boolean> {
        return await this._sessionManager.handleNavigation();
    }

    async initialize() {
        if (this._isInitialized) {
            console.info(
                "Workbench is already initialized. This might happen in strict mode due to useEffects being called multiple times.",
            );
            return;
        }
        this._isInitialized = true;

        this._navigationManager.start();
        await this._sessionManager.maybeOpenFromUrl();
    }

    beforeDestroy(): void {
        this._navigationManager.beforeDestroy();
        this._sessionManager.beforeDestroy();
    }
}
